import { useMemo } from 'react';
import { useConfiguredDefaultModifiers } from './useConfiguredDefaultModifiers.js';
import type { DSPopperJST } from '../react-desc-prop-types.js';

const noop = () => {};

const emptyObj = {};

interface UseConfiguredDefaultPropsT {
  props: DSPopperJST.Props;
  arrowElement: HTMLDivElement | null;
}

export const useConfiguredDefaultProps = ({ props, arrowElement }: Required<UseConfiguredDefaultPropsT>) => {
  // the global object 'document' is in scope only if the staticDefaultProps is defined inside the react hook
  //    moving this declaration outside of the hook will cause "body.document" to be undefined and cause an error
  // we exclude zIndex from the staticDefaultProps because it is being applied from styled-components theme.props.popper
  const staticDefaultProps: Omit<DSPopperJST.DefaultProps, 'zIndex'> = useMemo(
    () => ({
      referenceElement: null,
      showPopover: false,
      closeContextMenu: noop,
      onClickOutside: noop,
      withoutPortal: false,
      withoutArrow: false,
      withoutAnimation: false,
      animationDuration: 100,
      portalDOMContainer: document.body,
      startPlacementPreference: 'top',
      placementOrderPreference: ['top', 'bottom', 'left', 'right'],
      extraPopperStyles: emptyObj,
      customOffset: [0, 14],
    }),
    [],
  );
  const { modifiers: configuredModifiers } = useConfiguredDefaultModifiers({
    props,
    arrowElement,
    defaultProps: staticDefaultProps,
  });

  const defaultProps = useMemo(
    () => ({ ...staticDefaultProps, modifiers: configuredModifiers }),
    [configuredModifiers, staticDefaultProps],
  );

  return defaultProps;
};
